import { useCallback, useEffect, useState } from 'react';
import api from '../../API';
import { useAuth } from '../../providers/AuthContext';
import styles from './FolderDestinationPicker.module.css';

/**
 * Modal browser for picking a Girder destination.
 *
 * Props:
 *   mode     — 'folder' (must pick a folder) | 'parent' (folder, collection, or user)
 *   onSelect — ({ id, name, type, pathLabels }) => void
 *   onCancel — () => void
 *   title    — optional dialog title
 */
function FolderDestinationPicker({
    mode = 'folder',
    onSelect,
    onCancel,
    title,
}) {
    const { user } = useAuth();
    const [stack, setStack] = useState([{ id: null, name: 'Home', type: 'root' }]);
    const [collections, setCollections] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(false);

    const current = stack[stack.length - 1];
    const pathLabels = stack.slice(1).map((e) => e.name);

    const loadLevel = useCallback((level) => {
        setLoading(true);
        setCollections([]);
        setFolders([]);

        if (level.type === 'root') {
            setLoading(false);
            return;
        }
        if (level.type === 'collectionsRoot') {
            api.get('collection', { query: { limit: 100 } })
                .then((r) => {
                    if (r.status === 200) setCollections(r.data || []);
                })
                .catch(() => {})
                .finally(() => setLoading(false));
            return;
        }
        if (level.type === 'userRoot') {
            if (!user?._id) {
                setLoading(false);
                return;
            }
            api.get('folder', {
                query: { parentType: 'user', parentId: user._id, limit: 100 },
            })
                .then((r) => {
                    if (r.status === 200) setFolders(r.data || []);
                })
                .catch(() => {})
                .finally(() => setLoading(false));
            return;
        }
        if (level.type === 'collection') {
            api.get('folder', {
                query: { parentType: 'collection', parentId: level.id, limit: 100 },
            })
                .then((r) => {
                    if (r.status === 200) setFolders(r.data || []);
                })
                .catch(() => {})
                .finally(() => setLoading(false));
            return;
        }
        // folder
        api.get('folder', {
            query: { parentType: 'folder', parentId: level.id, limit: 100 },
        })
            .then((r) => {
                if (r.status === 200) setFolders(r.data || []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [user?._id]);

    useEffect(() => {
        loadLevel(current);
    }, [current, loadLevel]);

    function navigateInto(entry) {
        setStack((s) => [...s, entry]);
    }

    function navigateTo(idx) {
        setStack((s) => s.slice(0, idx + 1));
    }

    function confirmCurrent() {
        if (current.type === 'folder') {
            onSelect({
                id: current.id,
                name: current.name,
                type: 'folder',
                pathLabels,
            });
            return;
        }
        if (mode === 'parent' && current.type === 'collection') {
            onSelect({
                id: current.id,
                name: current.name,
                type: 'collection',
                pathLabels,
            });
            return;
        }
        if (mode === 'parent' && current.type === 'userRoot' && user?._id) {
            onSelect({
                id: user._id,
                name: 'My folders',
                type: 'user',
                pathLabels: ['My folders'],
            });
        }
    }

    const canUseCurrent =
        current.type === 'folder'
        || (mode === 'parent' && current.type === 'collection')
        || (mode === 'parent' && current.type === 'userRoot' && !!user?._id);

    const dialogTitle = title || (mode === 'parent' ? 'Choose parent' : 'Choose folder');

    return (
        <div
            className={styles.overlay}
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label={dialogTitle}>
                <div className={styles.header}>
                    <h3 className={styles.title}>{dialogTitle}</h3>
                    <button type="button" className={styles.closeButton} onClick={onCancel} aria-label="Close">
                        ✕
                    </button>
                </div>

                <nav className={styles.breadcrumb} aria-label="Browser navigation">
                    {stack.map((entry, idx) => (
                        <span key={idx} className={styles.breadcrumbEntry}>
                            {idx > 0 && <span className={styles.breadcrumbSep}>/</span>}
                            {idx < stack.length - 1 ? (
                                <button
                                    type="button"
                                    className={styles.breadcrumbLink}
                                    onClick={() => navigateTo(idx)}
                                >
                                    {entry.name}
                                </button>
                            ) : (
                                <span className={styles.breadcrumbCurrent}>{entry.name}</span>
                            )}
                        </span>
                    ))}
                </nav>

                <div className={styles.list}>
                    {loading ? (
                        <p className={styles.empty}>Loading…</p>
                    ) : current.type === 'root' ? (
                        <>
                            <button
                                type="button"
                                className={styles.row}
                                onClick={() => navigateInto({
                                    id: 'collections',
                                    name: 'Collections',
                                    type: 'collectionsRoot',
                                })}
                            >
                                <span className={styles.navButton}>Collections</span>
                            </button>
                            <button
                                type="button"
                                className={styles.row}
                                onClick={() => navigateInto({
                                    id: user?._id || 'user',
                                    name: 'My folders',
                                    type: 'userRoot',
                                })}
                            >
                                <span className={styles.navButton}>My folders</span>
                            </button>
                        </>
                    ) : (
                        <>
                            {collections.map((c) => (
                                <button
                                    key={c._id}
                                    type="button"
                                    className={styles.row}
                                    onClick={() => navigateInto({
                                        id: c._id,
                                        name: c.name,
                                        type: 'collection',
                                    })}
                                >
                                    <span className={styles.navButton}>{c.name}</span>
                                    <span className={styles.typeBadge}>collection</span>
                                </button>
                            ))}
                            {folders.map((f) => (
                                <button
                                    key={f._id}
                                    type="button"
                                    className={styles.row}
                                    onClick={() => navigateInto({
                                        id: f._id,
                                        name: f.name,
                                        type: 'folder',
                                    })}
                                >
                                    <span className={styles.navButton}>{f.name}</span>
                                    <span className={styles.typeBadge}>folder</span>
                                </button>
                            ))}
                            {!collections.length && !folders.length ? (
                                <p className={styles.empty}>No folders here.</p>
                            ) : null}
                        </>
                    )}
                </div>

                <div className={styles.footer}>
                    <span className={styles.selectionSummary}>
                        {canUseCurrent
                            ? `Use “${current.name}”`
                            : mode === 'folder'
                                ? 'Open a folder to select it'
                                : 'Open a folder, collection, or My folders'}
                    </span>
                    <div className={styles.footerActions}>
                        <button type="button" className={styles.buttonSecondary} onClick={onCancel}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.buttonPrimary}
                            disabled={!canUseCurrent}
                            onClick={confirmCurrent}
                        >
                            Select
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FolderDestinationPicker;
